import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/base/ComicCard";
import ComicFilter, { type ComicFilters } from "@/components/feature/ComicFilter";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { filterComics, getHotComics } from "@/lib/comicUtils";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const [input, setInput] = useState(query);
  const [filters, setFilters] = useState<ComicFilters>({
    genre: "全部",
    region: "全部",
    status: "全部",
    sort: "updated",
  });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(Boolean(query));

  useEffect(() => {
    setInput(query);
  }, [query]);

  useEffect(() => {
    if (!query) {
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 280);
    return () => clearTimeout(timer);
  }, [query, filters]);

  const results = useMemo(
    () => (query ? filterComics({ ...filters, keyword: query }) : []),
    [query, filters]
  );

  const hot = getHotComics(8);

  useDocumentMeta({
    title: query
      ? `搜索 “${query}” - 歪歪漫画在线观看`
      : "搜索漫画 - 歪歪漫画在线观看",
    description: query
      ? `与“${query}”相关的漫画作品列表，支持在线阅读与分类筛选，尽在歪歪漫画在线观看。`
      : "在歪歪漫画在线观看搜索你想看的漫画，支持关键词检索与分类筛选。",
    keywords: query
      ? `${query}漫画,搜索${query},歪歪漫画在线观看`
      : "搜索漫画,歪歪漫画在线观看",
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    setSearched(true);
    if (trimmed) {
      setSearchParams({ q: trimmed });
    } else {
      setSearchParams({});
    }
  };

  const clearSearch = () => {
    setInput("");
    setSearched(false);
    setSearchParams({});
  };

  const updateFilters = (patch: Partial<ComicFilters>) =>
    setFilters((prev) => ({ ...prev, ...patch }));
  const resetFilters = () =>
    setFilters({ genre: "全部", region: "全部", status: "全部", sort: "updated" });

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar />
      <main className="mx-auto w-full max-w-6xl px-4 md:px-8 py-6">
        <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">搜索漫画</h1>
        <p className="mt-2 text-sm text-foreground-500">输入漫画标题关键词，快速找到你想看的作品。</p>

        <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3">
            <i className="ri-search-line text-lg text-foreground-500"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="搜索漫画标题，例如：星域少年"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            {input && (
              <button type="button" onClick={clearSearch} className="shrink-0 text-foreground-400 hover:text-foreground-700 cursor-pointer" aria-label="清除">
                <i className="ri-close-circle-line text-lg"></i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-8 py-3 text-sm text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-search-line"></i>
            搜索
          </button>
        </form>

        {searched && query && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-foreground-600">
            <span>搜索词：</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-primary-700">
              {query}
              <button type="button" onClick={clearSearch} className="cursor-pointer" aria-label="清除搜索词">
                <i className="ri-close-line"></i>
              </button>
            </span>
          </div>
        )}

        {query && (
          <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-background-200 pt-5">
            <ComicFilter value={filters} onChange={updateFilters} onReset={resetFilters} />
          </div>
        )}

        {!query ? (
          <div className="mt-8">
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <p className="mt-4 text-sm text-foreground-600">输入关键词开始搜索漫画</p>
              <p className="mt-1 text-xs text-foreground-400">支持按标题检索，并可结合类型、地区、状态筛选</p>
            </div>

            <h2 className="mb-4 mt-10 font-heading text-xl text-foreground-950">大家都在看</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {hot.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        ) : loading ? (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <div className="aspect-[5/7] w-full animate-pulse bg-background-200"></div>
                <div className="space-y-2 p-3">
                  <div className="h-3 w-3/4 animate-pulse rounded bg-background-200"></div>
                  <div className="h-3 w-1/2 animate-pulse rounded bg-background-200"></div>
                </div>
              </div>
            ))}
          </div>
        ) : results.length > 0 ? (
          <div className="mt-6">
            <p className="mb-4 text-sm text-foreground-500">
              找到 <span className="text-primary-600 font-medium">{results.length}</span> 部与“{query}”相关的作品
            </p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {results.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-8">
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <p className="mt-4 text-sm text-foreground-600">未找到与“{query}”匹配的漫画</p>
              <p className="mt-1 text-xs text-foreground-400">换个关键词试试，或调整筛选条件</p>
              <button
                type="button"
                onClick={() => {
                  resetFilters();
                  clearSearch();
                }}
                className="mt-5 inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置搜索
              </button>
            </div>

            <h2 className="mb-4 mt-10 font-heading text-xl text-foreground-950">热门漫画推荐</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {hot.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}